import { UserAvatar } from '@features/user-avatar';
import * as stylex from '@stylexjs/stylex';
import { FC, memo, useState, useEffect, useRef } from 'react';
import { styles } from './styles';
import { IconButton } from '@shared/ui/icon-button';
import { TrashIcon, DownloadIcon } from '@shared/assets/icons';
import { Checkbox } from '@shared/ui/kit';
import { InfoIcon } from '@shared/assets/icons/info';
import { Tooltip } from '@shared/ui/tooltip';
import { colors } from '@shared/tokens/colors.stylex';
import { formatCheckInTime } from '@shared/libs/format-check-in-time';
import { Ticket, InvitationStatusTicketEnum } from '@pages/event/api/useFetchTicketSegment';
import { useUpdateTickets } from '@pages/event/api/useUpdateTickets';
import useGetSettingsByEventId from '@features/api/use-get-settings';
import { useToast } from '@shared/hooks/useToast';
import { InvitationStatusBlock } from './ui/invitation-status-block';
import { ConfirmationAlert } from './ui/confirmation-alert';
import { shouldShowTicketPresenceStatus } from './lib/should-show-ticket-presence-status';

interface IProps {
  ticket: Ticket;
  name: string;
  position?: string;
  onDelete?: () => void;
  getTicketQR?: () => void;
  checked?: boolean;
  disabled?: boolean;
  onToggle?: () => void;
  eventDateEnd?: string;
  eventDateStart?: string;
  eventId?: number;
  refetchSegments?: () => void;
  refetchCategories?: () => void;
  onConfirmStatusChange?: () => void;
}

export const EventParticipantCard: FC<IProps> = memo(
  ({
    name,
    position,
    ticket,
    onDelete,
    getTicketQR,
    checked,
    disabled,
    onToggle,
    eventDateEnd,
    eventDateStart,
    eventId,
    refetchSegments,
    refetchCategories,
    onConfirmStatusChange,
  }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [showConfirmationAlert, setShowConfirmationAlert] = useState(false);
    const [localStatus, setLocalStatus] = useState<InvitationStatusTicketEnum | null>(null);
    const dropdownRef = useRef<HTMLButtonElement>(null);
    const { updateTickets, fetching: updateFetching } = useUpdateTickets();
    const { data: eventSettings } = useGetSettingsByEventId(eventId ?? 0);
    const { showToast } = useToast();

    const getInitials = (name: string): string => {
      const names = name.trim().split(' ');
      const initials = names.map((name: string) => name.charAt(0).toUpperCase());
      return initials.join('').slice(0, 2);
    };
    const isInactive = disabled;

    const { date: checkInDate, time: checkInTime } = formatCheckInTime(ticket.checkInTime);

    const getScanTypeLabel = (scanType?: string | null) => {
      if (!scanType) return '-';
      if (scanType === 'scan') return 'Сканирование';
      if (scanType.startsWith('manual')) return 'Ручной';
      return scanType;
    };

    const inviteStatus = localStatus ?? ticket.invitationStatus ?? null;

    // Проверка, что даты еще не прошли
    const checkDatesNotPassed = () => {
      if (!eventDateEnd) return true;
      const eventEndDate = new Date(eventDateEnd);
      const now = new Date();
      return eventEndDate > now;
    };

    const sendTicketAfterAcceptAt = eventSettings?.sendTicketAfterAcceptAt;
    const showTicketPresenceStatus = shouldShowTicketPresenceStatus(eventDateStart);
    // Обработка подтверждения измнеения статуса на 'Принято'
    const handleConfirmStatusChange = async () => {
      try {
        const variables: { ticketIds: number[]; invitationStatus: string } = {
          ticketIds: [ticket.id],
          invitationStatus: 'ACCEPTED',
        };

        const result = await updateTickets(variables);

        if (result.error) {
          showToast('Ошибка при обновлении статуса приглашения', 'error');
          setLocalStatus(null);
          setShowConfirmationAlert(false);
          return;
        }

        setShowConfirmationAlert(false);

        if (refetchSegments) {
          refetchSegments();
        }
        if (refetchCategories) {
          refetchCategories();
        }

        onConfirmStatusChange?.();
      } catch (error) {
        showToast('Ошибка при обновлении статуса приглашения', 'error');
        setLocalStatus(null);
        setShowConfirmationAlert(false);
      }
    };

    const handleCancelStatusChange = () => {
      setShowConfirmationAlert(false);
      setLocalStatus(null);
      setIsDropdownOpen(false);
    };
    // Обработка изменения статуса на 'Не принято'
    const confirmationAlertRef = useRef<HTMLDivElement | null>(null);

    const handleDeclineStatusChange = async () => {
      setIsDropdownOpen(false);
      setShowConfirmationAlert(false);

      if (!checkDatesNotPassed()) {
        return;
      }

      setLocalStatus(InvitationStatusTicketEnum.DECLINED);

      try {
        const variables: { ticketIds: number[]; invitationStatus: string } = {
          ticketIds: [ticket.id],
          invitationStatus: 'DECLINED',
        };

        const result = await updateTickets(variables);

        if (result.error) {
          showToast('Ошибка при обновлении статуса приглашения', 'error');
          setLocalStatus(null);
          return;
        }

        if (refetchSegments) {
          refetchSegments();
        }
        if (refetchCategories) {
          refetchCategories();
        }

        onConfirmStatusChange?.();
      } catch (error) {
        showToast('Ошибка при обновлении статуса приглашения', 'error');
        setLocalStatus(null);
      }
    };

    const handleSelectAccepted = () => {
      setIsDropdownOpen(false);

      setLocalStatus(InvitationStatusTicketEnum.ACCEPTED);

      if (checkDatesNotPassed()) {
        setShowConfirmationAlert(true);
        return;
      }

      handleConfirmStatusChange();
    };

    useEffect(() => {
      if (!showConfirmationAlert) return;

      const handleOutsideAlertClick = (event: MouseEvent) => {
        if (confirmationAlertRef.current && !confirmationAlertRef.current.contains(event.target as Node)) {
          handleCancelStatusChange();
        }
      };

      document.addEventListener('mousedown', handleOutsideAlertClick);

      return () => {
        document.removeEventListener('mousedown', handleOutsideAlertClick);
      };
    }, [showConfirmationAlert]);

    return (
      <div {...stylex.props(styles.container, disabled && styles.disabledRow)}>
        <div {...stylex.props(styles.containerTab)}>
          <div {...stylex.props(styles.checkbox)}>
            {isInactive ? (
              <input type="checkbox" disabled {...stylex.props(styles.checkboxDis)} />
            ) : (
              <Checkbox isChecked={!!checked} onChange={() => onToggle?.()} />
            )}
          </div>

          <UserAvatar initials={getInitials(name)} />
          <div {...stylex.props(styles.personCard)}>
            <div {...stylex.props(styles.personDescription)}>
              <div {...stylex.props(styles.firstName)}>{name}</div>
              {isInactive ? (
                <div {...stylex.props(styles.inactivePosition)}>
                  <span {...stylex.props(styles.inactivePositionText)}>Больше не работает</span>
                </div>
              ) : (
                <div {...stylex.props(styles.position)}>{position}</div>
              )}
            </div>
          </div>

          <div {...stylex.props(styles.statusWrapper)}>
            <div {...stylex.props(styles.statuses)}>
              <section {...stylex.props(styles.invitationStatusesBlock)}>
                <div {...stylex.props(styles.invitationStatusesItem)}>
                  <div {...stylex.props(styles.invitationStatusesTitleBlock)}>
                    <h5 {...stylex.props(styles.invitationTitle)}>Приглашение</h5>
                    <InvitationStatusBlock
                      ticket={{ ...ticket, invitationStatus: inviteStatus }}
                      isDropdownOpen={isDropdownOpen}
                      sendTicketAfterAcceptAt={sendTicketAfterAcceptAt}
                      dropdownRef={dropdownRef}
                      onToggleDropdown={() => setIsDropdownOpen(!isDropdownOpen)}
                      onCloseDropdown={() => setIsDropdownOpen(false)}
                      onSelectAccepted={handleSelectAccepted}
                      onSelectDeclined={handleDeclineStatusChange}
                      eventDateEnd={eventDateEnd}
                    />
                  </div>
                  <div {...stylex.props(styles.statusBlock)}>
                    <span {...stylex.props(styles.statusTitle)}>СМС: </span>
                    <span {...stylex.props(styles.sendStatus)}>
                      {ticket.smsInviteSent ? 'Отправлено' : 'Не отправлено'}
                    </span>
                  </div>
                  <div {...stylex.props(styles.statusBlock)}>
                    <span {...stylex.props(styles.statusTitle)}>E-mail: </span>
                    <span {...stylex.props(styles.sendStatus)}>
                      {ticket.emailInviteSent ? 'Отправлено' : 'Не отправлено'}
                    </span>
                  </div>
                </div>
              </section>
              <div {...stylex.props(styles.borderIcon)}></div>
              <section {...stylex.props(styles.invitationStatusesBlock)}>
                <div {...stylex.props(styles.invitationStatusesItem)}>
                  <div {...stylex.props(styles.invitationStatusesTitleBlock)}>
                    <h5 {...stylex.props(styles.invitationTitle)}>Билет</h5>
                    {showTicketPresenceStatus && (
                      <div
                        {...stylex.props(
                          styles.invitationStatusBlock,
                          !ticket.checked && styles.invitationStatusBlockWarning
                        )}
                      >
                        <span {...stylex.props(styles.invitationStatus)}>
                          {ticket.checked ? 'Присутствовал' : 'Не присутствовал'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div {...stylex.props(styles.statusBlock)}>
                    <span {...stylex.props(styles.statusTitle)}>СМС: </span>
                    <span {...stylex.props(styles.sendStatus)}>
                      {ticket.smsTicketSent ? 'Отправлено' : 'Не отправлено'}
                    </span>
                  </div>
                  <div {...stylex.props(styles.statusBlock)}>
                    <span {...stylex.props(styles.statusTitle)}>E-mail: </span>
                    <span {...stylex.props(styles.sendStatus)}>
                      {ticket.emailTicketSent ? 'Отправлено' : 'Не отправлено'}
                    </span>
                  </div>
                </div>
              </section>
            </div>
            {showConfirmationAlert && (
              <div ref={confirmationAlertRef}>
                <ConfirmationAlert
                  sendTicketAfterAcceptAt={sendTicketAfterAcceptAt}
                  onCancel={handleCancelStatusChange}
                  onConfirm={() => handleConfirmStatusChange()}
                  isLoading={updateFetching}
                />
              </div>
            )}
          </div>
          <div {...stylex.props(styles.actions)}>
            <div {...stylex.props(styles.actionsIcon)}>
              <IconButton icon={<TrashIcon color={colors.tertiaryIcon} />} onClick={onDelete} />
            </div>

            <div {...stylex.props(styles.actionsIconStroke)}>
              <Tooltip
                position="topRight"
                title={
                  <div {...stylex.props(styles.infoTooltip)}>
                    <div {...stylex.props(styles.infoRow)}>
                      <span {...stylex.props(styles.titleRow)}>Регистрация:</span>
                      <span {...stylex.props(styles.statusRow)}>
                        {checkInDate !== '-' && checkInTime !== '-' ? `${checkInDate} в ${checkInTime}` : '-'}
                      </span>
                    </div>
                    <div {...stylex.props(styles.infoRow)}>
                      <span {...stylex.props(styles.titleRow)}>Регистратор:</span>
                      <span {...stylex.props(styles.statusRow)}>{ticket.organizerName || '-'}</span>
                    </div>
                    <div {...stylex.props(styles.infoRow)}>
                      <span {...stylex.props(styles.titleRow)}>Способ:</span>
                      <span {...stylex.props(styles.statusRow)}>{getScanTypeLabel(ticket.scanType)}</span>
                    </div>
                  </div>
                }
              >
                <IconButton icon={<InfoIcon />} />
              </Tooltip>
            </div>

            <div {...stylex.props(styles.actionsIcon)}>
              <IconButton icon={<DownloadIcon />} onClick={getTicketQR} />
            </div>
          </div>
        </div>
      </div>
    );
  }
);
